import { motion } from "framer-motion";
import { Check, Minus } from "lucide-react";

const ROWS = [
  { feature: "Co-branding option available", lionforce: true, others: false },
  { feature: "Transparent monthly pricing (no hidden fees)", lionforce: true, others: false },
  { feature: "Flexible exit - no long lock-in", lionforce: true, others: false },
  { feature: "Dedicated admin & operations executive", lionforce: true, others: false },
  { feature: "Full statutory compliance management", lionforce: true, others: true },
  { feature: "Payroll & HRMS setup", lionforce: true, others: true },
  { feature: "Real estate / office setup included (optional)", lionforce: true, others: false },
  { feature: "24/7 local support with single point of contact", lionforce: true, others: false },
];

const Mark = ({ yes, testid }) =>
  yes ? (
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#5B4199]" data-testid={testid}>
      <Check className="h-4 w-4 text-white" />
    </span>
  ) : (
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#0B132B]/[0.06]" data-testid={testid}>
      <Minus className="h-4 w-4 text-[#94A3B8]" />
    </span>
  );

export default function Comparison() {
  return (
    <section id="compare" className="relative -mt-8 rounded-t-[48px] py-20 sm:py-28 lg:py-32 bg-[#F4F0FE]" data-testid="comparison-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-12"
        >
          <span className="inline-flex items-center rounded-full border border-[#5B4199]/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#5B4199] mb-6">
            Comparison
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-7xl font-extrabold tracking-tight text-[#0B132B] leading-[1.04]">
            Compare the operating choices.
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-[32px] bg-white overflow-hidden shadow-[0_28px_64px_-24px_rgba(76,29,149,0.22)]"
          data-testid="comparison-table"
        >
          <div className="hidden sm:grid grid-cols-[1fr_130px_160px] items-center gap-4 px-8 py-5 border-b border-[#0B132B]/[0.07] bg-[#F6F3FE]">
            <span className="text-[11px] font-mono-tech uppercase tracking-[0.18em] text-[#94A3B8]">Feature</span>
            <span className="text-[11px] font-mono-tech uppercase tracking-[0.18em] text-[#5B4199] text-center">Expante</span>
            <span className="text-[11px] font-mono-tech uppercase tracking-[0.18em] text-[#94A3B8] text-center">Other providers</span>
          </div>
          <ul className="divide-y divide-[#0B132B]/[0.05]">
            {ROWS.map((r, i) => (
              <li
                key={r.feature}
                className="grid grid-cols-1 sm:grid-cols-[1fr_130px_160px] items-center gap-2 sm:gap-4 px-8 py-4 hover:bg-[#F6F3FE] transition-colors"
                data-testid={`comparison-row-${i}`}
              >
                <span className="text-sm sm:text-[15px] font-medium text-[#0B132B]">{r.feature}</span>
                <span className="flex items-center gap-2 sm:justify-center">
                  <span className="sm:hidden text-[10px] font-mono-tech uppercase tracking-widest text-[#5B4199] w-24">Expante</span>
                  <Mark yes={r.lionforce} testid={`comparison-mark-expante-${i}`} />
                </span>
                <span className="flex items-center gap-2 sm:justify-center">
                  <span className="sm:hidden text-[10px] font-mono-tech uppercase tracking-widest text-[#94A3B8] w-24">Others</span>
                  <Mark yes={r.others} testid={`comparison-mark-others-${i}`} />
                </span>
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-6 text-xs text-[#94A3B8] leading-relaxed max-w-3xl"
        >
          As published by Lionforce for its India expansion services. “Other providers” is a general
          market reference, not a named competitor; scope varies by provider and engagement.
        </motion.p>
      </div>
    </section>
  );
}
